import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Sparkles, Home as HomeIcon, Shirt } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_decor-and-style/artifacts/89wv37zw_LOGO.png";

const HERO_IMG = "https://images.unsplash.com/photo-1644375857059-e5b368156377?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxtYW4lMjBmb3JtYWwlMjBzdWl0JTIwd2F0Y2glMjBsdXh1cnklMjBkYXJrJTIwYmFja2dyb3VuZCUyMHBvcnRyYWl0fGVufDB8fHxibGFja19hbmRfd2hpdGV8MTc3NTg5MjMwNXww&ixlib=rb-4.1.0&q=85";

const CATEGORY_IMAGES = {
  fashion: "https://images.unsplash.com/photo-1614252368727-99517bc90d7b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxzdHlsaXNoJTIwbWFuJTIwYXZpYXRvciUyMHN1bmdsYXNzZXMlMjBibGF6ZXIlMjBqYWNrZXQlMjBmYXNoaW9ufGVufDB8fHx8MTc3NTg5MjMwNXww&ixlib=rb-4.1.0&q=85",
  lifestyle: "https://images.unsplash.com/photo-1501555790667-ba7ea28b4cea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBtaW5pbWFsaXN0JTIwd2F0Y2glMjBmbGF0bGF5fGVufDB8fHx8MTc3NTg5MDk4Nnww&ixlib=rb-4.1.0&q=85",
  "home-decor": "https://images.unsplash.com/photo-1638972691611-69633a3d3127?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBob21lJTIwZGVjb3IlMjBpbnRlcmlvcnxlbnwwfHx8fDE3NzU4OTA5ODV8MA&ixlib=rb-4.1.0&q=85",
  other: "https://images.unsplash.com/photo-1583854327660-edc26bc594ed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwyfHxzdHlsaXNoJTIwbWFuJTIwYXZpYXRvciUyMHN1bmdsYXNzZXMlMjBibGF6ZXIlMjBqYWNrZXQlMjBmYXNoaW9ufGVufDB8fHx8MTc3NTg5MjMwNXww&ixlib=rb-4.1.0&q=85",
};

const CATEGORIES = [
  { label: 'Fashion', value: 'fashion', icon: Shirt, desc: 'Trending styles, outfits & accessories' },
  { label: 'Lifestyle', value: 'lifestyle', icon: Sparkles, desc: 'Curated picks for everyday living' },
  { label: 'Home Decor', value: 'home-decor', icon: HomeIcon, desc: 'Transform your space beautifully' },
];

export default function Home() {
  const [articles, setArticles] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [audienceForm, setAudienceForm] = useState({ name: '', email: '' });
  const [testimonialForm, setTestimonialForm] = useState({ name: '', review: '', rating: 5 });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetch(`${API}/articles?status=published`).then(r => r.json()).then(setArticles).catch(() => {});
    fetch(`${API}/testimonials?approved_only=true`).then(r => r.json()).then(setTestimonials).catch(() => {});
  }, []);

  const handleAudienceSubmit = async (e) => {
    e.preventDefault();
    if (!audienceForm.name || !audienceForm.email) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/audience`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(audienceForm),
      });
      if (res.ok) {
        toast.success('Thank you for subscribing!');
        setAudienceForm({ name: '', email: '' });
      }
    } catch {
      toast.error('Something went wrong');
    }
    setSubmitting(false);
  };

  const handleTestimonialSubmit = async (e) => {
    e.preventDefault();
    if (!testimonialForm.name || !testimonialForm.review) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/testimonials`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(testimonialForm),
      });
      if (res.ok) {
        toast.success('Thank you for your review! It will appear after approval.');
        setTestimonialForm({ name: '', review: '', rating: 5 });
      }
    } catch {
      toast.error('Something went wrong');
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Hero" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/40" />
        </div>
        <div className="relative z-10 text-center px-6 max-w-4xl">
          <h1 className="font-['Playfair_Display'] text-5xl md:text-6xl lg:text-7xl font-black text-white tracking-tighter leading-none mb-6 opacity-0 animate-fade-in-up">
            Style & Space
          </h1>
          <p className="text-white/70 text-base md:text-lg max-w-2xl mx-auto mb-10 opacity-0 animate-fade-in-up stagger-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Your curated destination for fashion inspiration, lifestyle essentials, and home decor ideas. Handpicked products with exclusive affiliate deals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center opacity-0 animate-fade-in-up stagger-2">
            <Link
              to="/articles/fashion"
              className="bg-white text-[#0A1128] px-8 py-3 text-sm uppercase tracking-widest font-semibold hover:bg-white/90 transition-all inline-flex items-center justify-center gap-2"
              data-testid="hero-explore-btn"
            >
              Explore Articles <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="#about"
              className="border border-white/40 text-white px-8 py-3 text-sm uppercase tracking-widest font-medium hover:bg-white/10 transition-all"
              data-testid="hero-learn-btn"
            >
              Learn More
            </a>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 md:py-32 bg-white" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <p className="overline text-[#6B7280] mb-4">What We Do</p>
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#0A1128] tracking-tight mb-6">
                Curated Style. Elevated Living.
              </h2>
              <p className="text-[#6B7280] leading-relaxed mb-6">
                Style & Space is your one-stop destination for discovering the latest in fashion trends, lifestyle products, and home decor inspiration. We handpick the best products and create in-depth articles to help you make informed choices.
              </p>
              <ul className="space-y-3 text-[#0A1128]">
                <li className="flex items-start gap-3">
                  <Shirt className="w-5 h-5 mt-0.5 text-[#0A1128]" />
                  <span className="text-sm"><strong>Fashion Forward</strong> - Latest trends in clothing, accessories, and footwear</span>
                </li>
                <li className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 mt-0.5 text-[#0A1128]" />
                  <span className="text-sm"><strong>Lifestyle Picks</strong> - Watches, gadgets, and everyday essentials</span>
                </li>
                <li className="flex items-start gap-3">
                  <HomeIcon className="w-5 h-5 mt-0.5 text-[#0A1128]" />
                  <span className="text-sm"><strong>Home Decor</strong> - Transform your space with curated interiors</span>
                </li>
              </ul>
            </div>
            <div className="relative">
              <div className="img-hover-zoom">
                <img
                  src="https://images.unsplash.com/photo-1638972691611-69633a3d3127?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBob21lJTIwZGVjb3IlMjBpbnRlcmlvcnxlbnwwfHx8fDE3NzU4OTA5ODV8MA&ixlib=rb-4.1.0&q=85"
                  alt="Curated living"
                  className="w-full aspect-[4/5] object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-[#0A1128] text-white px-8 py-6 hidden md:block">
                <p className="font-['Playfair_Display'] text-3xl font-bold">100+</p>
                <p className="text-white/60 text-xs uppercase tracking-widest mt-1">Curated Products</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-24 bg-[#F4F4F4]" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="overline text-[#6B7280] mb-4 text-center">Browse</p>
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#0A1128] tracking-tight text-center mb-16">
            Explore Categories
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {CATEGORIES.map((cat, i) => (
              <Link
                key={cat.value}
                to={`/articles/${cat.value}`}
                className={`group relative overflow-hidden opacity-0 animate-fade-in-up stagger-${i + 1}`}
                data-testid={`home-category-${cat.value}`}
              >
                <div className="img-hover-zoom aspect-[3/4]">
                  <img
                    src={CATEGORY_IMAGES[cat.value]}
                    alt={cat.label}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <cat.icon className="w-6 h-6 text-white mb-3" />
                  <h3 className="font-['Playfair_Display'] text-2xl font-bold text-white mb-2">{cat.label}</h3>
                  <p className="text-white/60 text-sm">{cat.desc}</p>
                  <div className="mt-4 flex items-center gap-2 text-white/80 text-xs uppercase tracking-widest group-hover:text-white transition-colors">
                    Explore <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Articles */}
      {articles.length > 0 && (
        <section className="py-24 bg-white" data-testid="latest-articles-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <p className="overline text-[#6B7280] mb-4">Latest</p>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#0A1128] tracking-tight mb-16">
              Featured Articles
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {articles.slice(0, 6).map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group"
                  data-testid={`article-card-${article.slug}`}
                >
                  <div className="img-hover-zoom aspect-[4/3] mb-4">
                    <img
                      src={article.cover_image || CATEGORY_IMAGES[article.category] || HERO_IMG}
                      alt={article.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <p className="overline text-[#6B7280] mb-2">{article.category?.replace('-', ' ')}</p>
                  <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#0A1128] group-hover:text-[#6B7280] transition-colors leading-snug">
                    {article.title}
                  </h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials */}
      <section className="py-24 bg-[#0A1128]" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="overline text-white/40 mb-4 text-center">Testimonials</p>
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-white tracking-tight text-center mb-16">
            What Our Readers Say
          </h2>

          {testimonials.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
              {testimonials.map((t) => (
                <div key={t.id} className="border border-white/10 p-8" data-testid={`testimonial-${t.id}`}>
                  <div className="flex gap-1 mb-4">
                    {[...Array(t.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-[#C9A96E] fill-[#C9A96E]" />
                    ))}
                  </div>
                  <p className="text-white/70 text-sm leading-relaxed mb-6">"{t.review}"</p>
                  <p className="text-white font-medium text-sm">{t.name}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-white/40 text-center mb-16 text-sm">Be the first to leave a review!</p>
          )}

          {/* Testimonial Form */}
          <div className="max-w-lg mx-auto">
            <h3 className="font-['Playfair_Display'] text-xl text-white text-center mb-8">Share Your Experience</h3>
            <form onSubmit={handleTestimonialSubmit} className="space-y-4" data-testid="testimonial-form">
              <Input
                placeholder="Your Name"
                value={testimonialForm.name}
                onChange={e => setTestimonialForm(f => ({ ...f, name: e.target.value }))}
                className="bg-transparent border-0 border-b border-white/20 rounded-none text-white placeholder:text-white/30 focus-visible:ring-0 focus-visible:border-white/60"
                data-testid="testimonial-name-input"
              />
              <textarea
                placeholder="Your Review"
                value={testimonialForm.review}
                onChange={e => setTestimonialForm(f => ({ ...f, review: e.target.value }))}
                rows={3}
                className="w-full bg-transparent border-0 border-b border-white/20 text-white placeholder:text-white/30 focus:outline-none focus:border-white/60 text-sm py-2 resize-none"
                data-testid="testimonial-review-input"
              />
              <div className="flex items-center gap-2">
                <span className="text-white/40 text-xs uppercase tracking-widest">Rating:</span>
                {[1, 2, 3, 4, 5].map(r => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setTestimonialForm(f => ({ ...f, rating: r }))}
                    data-testid={`rating-star-${r}`}
                  >
                    <Star className={`w-5 h-5 transition-colors ${r <= testimonialForm.rating ? 'text-[#C9A96E] fill-[#C9A96E]' : 'text-white/20'}`} />
                  </button>
                ))}
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-white text-[#0A1128] hover:bg-white/90 rounded-none uppercase tracking-widest text-sm py-3"
                data-testid="testimonial-submit-btn"
              >
                {submitting ? 'Submitting...' : 'Submit Review'}
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Audience / Subscribe */}
      <section className="py-24 bg-white" data-testid="subscribe-section">
        <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
          <p className="overline text-[#6B7280] mb-4">Stay Updated</p>
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#0A1128] tracking-tight mb-4">
            Join Our Community
          </h2>
          <p className="text-[#6B7280] text-sm mb-10">
            Get the latest articles on fashion, lifestyle, and home decor delivered to your inbox. No spam, just style.
          </p>
          <form onSubmit={handleAudienceSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-testid="audience-form">
            <Input
              placeholder="Your Name"
              value={audienceForm.name}
              onChange={e => setAudienceForm(f => ({ ...f, name: e.target.value }))}
              className="border-[#0A1128] rounded-none focus-visible:ring-[#0A1128]"
              data-testid="audience-name-input"
            />
            <Input
              type="email"
              placeholder="Email Address"
              value={audienceForm.email}
              onChange={e => setAudienceForm(f => ({ ...f, email: e.target.value }))}
              className="border-[#0A1128] rounded-none focus-visible:ring-[#0A1128]"
              data-testid="audience-email-input"
            />
            <Button
              type="submit"
              disabled={submitting}
              className="bg-[#0A1128] text-white hover:bg-[#0A1128]/90 rounded-none uppercase tracking-widest text-sm px-8 whitespace-nowrap"
              data-testid="audience-submit-btn"
            >
              Subscribe
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
}
